import * as React from 'react';
import {
  Copy,
  Check,
  WrapText,
  Hash,
  Search,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, escapeHtml } from '@/lib/utils';
import {
  highlightCode,
  getLanguageFromPath,
  getLanguageName,
} from '@/lib/syntax-highlight';

export interface CodeViewerProps {
  content: string;
  filePath: string;
  language?: string;
  showLineNumbers?: boolean;
  className?: string;
}

/**
 * Full-featured code viewer with syntax highlighting, line numbers, search, and controls
 */
export function CodeViewer({
  content,
  filePath,
  language: providedLanguage,
  showLineNumbers: initialShowLineNumbers = true,
  className,
}: CodeViewerProps): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const [showLineNumbers, setShowLineNumbers] = React.useState(
    initialShowLineNumbers
  );
  const [wordWrap, setWordWrap] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showSearch, setShowSearch] = React.useState(false);
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const codeContainerRef = React.useRef<HTMLDivElement>(null);

  // Determine language
  const language = providedLanguage || getLanguageFromPath(filePath);
  const languageName = getLanguageName(language);

  // Split content into lines
  const lines = React.useMemo(() => content.split('\n'), [content]);

  // Find matches for search
  const matches = React.useMemo(() => {
    if (!searchTerm) return [];
    const results: Array<{ lineIndex: number; positions: number[] }> = [];

    lines.forEach((line, lineIndex) => {
      const positions: number[] = [];
      let index = line.toLowerCase().indexOf(searchTerm.toLowerCase());
      while (index !== -1) {
        positions.push(index);
        index = line
          .toLowerCase()
          .indexOf(searchTerm.toLowerCase(), index + 1);
      }
      if (positions.length > 0) {
        results.push({ lineIndex, positions });
      }
    });

    return results;
  }, [lines, searchTerm]);

  // Total match count
  const totalMatches = matches.reduce(
    (sum, match) => sum + match.positions.length,
    0
  );

  // Highlight code with search matches
  const highlightedLines = React.useMemo(() => {
    return lines.map((line, lineIndex) => {
      let highlightedLine = highlightCode(line || ' ', language);

      // Apply search highlighting if there are matches
      if (searchTerm && matches.length > 0) {
        const match = matches.find((m) => m.lineIndex === lineIndex);
        if (match) {
          // We need to inject search highlights into already-highlighted HTML
          // This is complex, so we'll use a simpler approach: wrap matches in the original line
          const parts: string[] = [];
          let lastIndex = 0;

          match.positions.forEach((pos) => {
            if (pos > lastIndex) {
              // SECURITY: Escape HTML to prevent XSS
              parts.push(escapeHtml(line.slice(lastIndex, pos)));
            }
            // SECURITY: Escape the matched text before wrapping in mark tag
            parts.push(
              `<mark class="bg-yellow-300 dark:bg-yellow-600 text-foreground">${escapeHtml(
                line.slice(pos, pos + searchTerm.length)
              )}</mark>`
            );
            lastIndex = pos + searchTerm.length;
          });

          if (lastIndex < line.length) {
            // SECURITY: Escape HTML to prevent XSS
            parts.push(escapeHtml(line.slice(lastIndex)));
          }

          // Re-highlight the modified line
          const markedLine = parts.join('');
          highlightedLine = markedLine;
        }
      }

      return highlightedLine;
    });
  }, [lines, language, searchTerm, matches]);

  // Copy to clipboard
  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [content]);

  // Toggle search
  const handleToggleSearch = React.useCallback(() => {
    setShowSearch((prev) => !prev);
    if (!showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchTerm('');
      setCurrentMatch(0);
    }
  }, [showSearch]);

  // Navigate search matches
  const handleNextMatch = React.useCallback(() => {
    if (totalMatches === 0) return;
    setCurrentMatch((prev) => (prev + 1) % totalMatches);
  }, [totalMatches]);

  const handlePrevMatch = React.useCallback(() => {
    if (totalMatches === 0) return;
    setCurrentMatch((prev) => (prev - 1 + totalMatches) % totalMatches);
  }, [totalMatches]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        handleToggleSearch();
      }
      if (showSearch && e.key === 'Escape') {
        setShowSearch(false);
        setSearchTerm('');
      }
      if (showSearch && e.key === 'Enter') {
        if (e.shiftKey) {
          handlePrevMatch();
        } else {
          handleNextMatch();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, handleToggleSearch, handleNextMatch, handlePrevMatch]);

  // Scroll to current match
  React.useEffect(() => {
    if (matches.length === 0 || !codeContainerRef.current) return;

    let matchCount = 0;
    for (const match of matches) {
      for (let i = 0; i < match.positions.length; i++) {
        if (matchCount === currentMatch) {
          // Scroll to this line
          const lineElement = codeContainerRef.current.querySelector(
            `[data-line="${match.lineIndex}"]`
          );
          if (lineElement) {
            lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        matchCount++;
      }
    }
  }, [currentMatch, matches]);

  return (
    <div className={cn('border rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 bg-muted/50 border-b">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge variant="secondary" className="shrink-0">
            {languageName}
          </Badge>
          <span className="text-sm text-muted-foreground truncate">
            {filePath}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowLineNumbers(!showLineNumbers)}
            title="Toggle line numbers"
            className="h-8 w-8"
          >
            <Hash className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setWordWrap(!wordWrap)}
            title="Toggle word wrap"
            className="h-8 w-8"
          >
            <WrapText className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleSearch}
            title="Search (Ctrl+F)"
            className="h-8 w-8"
          >
            <Search className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopy}
            title="Copy code"
            className="h-8 w-8"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search in file..."
            className="flex-1 bg-transparent border-none outline-none text-sm"
          />
          {totalMatches > 0 && (
            <span className="text-xs text-muted-foreground shrink-0">
              {currentMatch + 1} / {totalMatches}
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevMatch}
            disabled={totalMatches === 0}
            title="Previous match (Shift+Enter)"
            className="h-6 w-6"
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextMatch}
            disabled={totalMatches === 0}
            title="Next match (Enter)"
            className="h-6 w-6"
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowSearch(false);
              setSearchTerm('');
            }}
            title="Close search (Esc)"
            className="h-6 w-6"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Code Content */}
      <div
        ref={codeContainerRef}
        className="overflow-auto max-h-[600px] bg-background"
      >
        <pre
          className={cn(
            'p-4 text-sm font-mono',
            wordWrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'
          )}
        >
          {highlightedLines.map((lineContent, index) => (
            <div
              key={index}
              data-line={index}
              className="flex hover:bg-muted/50"
            >
              {showLineNumbers && (
                <span className="select-none text-muted-foreground text-right pr-4 shrink-0 w-12">
                  {index + 1}
                </span>
              )}
              <code
                className={`language-${language} flex-1`}
                dangerouslySetInnerHTML={{ __html: lineContent }}
              />
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}
